// Eskom PARCC — Dashboard (audit recovery command centre), redesigned.
//
// Round-2 UI/UX overhaul (owner feedback 4): a refined, theme-aware command
// centre — summary stat cards, an interactive compliance donut, a risk gauge,
// breach-by-category + division-risk bars, and a recurring-compliance trend.
// The DATA wiring is unchanged: the full population is fetched read-only, scoped
// by role, filtered client-side via the shared FacetFilterBar, and every section
// recomputes from the filtered scope (computeDashboard + derive.ts). No writes.
import { useMemo, useState } from 'react';
import {
  Database,
  CheckCircle2,
  ClipboardList,
  Banknote,
  XCircle,
  MinusCircle,
  Download,
  FileCheck,
} from 'lucide-react';
import { LoadingState, ErrorState, EmptyState, Button, useToast } from '../../components/ui';
import { buildDrill, type DrillKey } from './drill';
import { DrillPanel } from './DrillPanel';
import {
  DistributionDonut,
  CategoryBar,
  StackedCategoryBar,
  RiskGauge,
} from '../../components/ui/charts';
import { FacetFilterBar } from '../../components/ui/FacetFilterBar';
import { useAuth } from '../../lib/auth';
import { useThemeColors } from '../../lib/useThemeColors';
import { formatRandCompact } from '../../lib/money';
import { scopeTransactions } from '../../lib/permissions';
import { useTransactions, useFindings } from '../../hooks';
import { computeDashboard } from '../../domain/dashboard';
import { effectiveComplianceByRef } from '../transactions/compliance';
import { genuineFindings, summariseFindings, findingOpen } from '../../domain/findings';
import { buildSelfDeclaredRefs } from '../../domain/gaps';
import { EMPTY_FACETS, matchesFacets, type FacetState, type FacetRow } from '../../domain/filters';
import {
  breachBreakdown,
  recurringFindingsByTheme,
  divisionStats,
  BREACH_SPLIT_FOOTNOTE,
  type BreachBucketKey,
} from './derive';
import { DISTRIBUTION_LEVELS, levelColour } from './palette';
import { buildDashboardExportModel, riskBand } from './export/dashboardExportModel';
import { workbookFromSheets, downloadWorkbook } from '../exports';

const BREACH_COLOUR: Record<BreachBucketKey, (c: ReturnType<typeof useThemeColors>) => string> = {
  pfma: (c) => c.bad,
  policy: (c) => c.warn,
  legislative: (c) => c.accent,
};

export function DashboardPage(): React.JSX.Element {
  const transactionsQuery = useTransactions();
  const findingsQuery = useFindings();
  const { grant } = useAuth();
  const colors = useThemeColors();
  const toast = useToast();
  const [facets, setFacets] = useState<FacetState>(EMPTY_FACETS);
  const [busy, setBusy] = useState<null | 'pptx' | 'pdf' | 'xlsx'>(null);
  // Drill-through: which headline figure the reader opened. One key for every
  // clickable widget (see drill.ts); null = no panel.
  const [drillKey, setDrillKey] = useState<DrillKey | null>(null);
  // Admin / manager / team lead may download the dashboard to enhance their
  // reports (Lindiwe R4 #1 + #4). Reviewers keep their scoped on-screen cockpit.
  const canExport =
    grant?.role === 'admin' || grant?.role === 'manager' || grant?.role === 'teamLead';

  const transactions = useMemo(
    () => (transactionsQuery.data ?? []).filter((t) => !t.deleted),
    [transactionsQuery.data],
  );
  const visible = useMemo(() => scopeTransactions(transactions, grant), [transactions, grant]);
  const findings = useMemo(() => findingsQuery.data ?? [], [findingsQuery.data]);

  // referenceNo → EFFECTIVE compliance level (findings + strict-rule override rolled
  // up), computed over the full non-deleted finding set exactly as the Transactions
  // register does. Every level-based figure below reads this, so the dashboard's
  // Non-Compliant / Possible-IE counts (and the compliance filter) match the register
  // instead of undercounting from the sparse stored `complianceLevel` column.
  const effLevelByRef = useMemo(
    () => effectiveComplianceByRef(visible, findings),
    [visible, findings],
  );

  const rows: FacetRow[] = useMemo(
    () =>
      visible.map((tx) => ({
        tx,
        compliance: effLevelByRef.get(tx.referenceNo) || tx.complianceLevel || 'Pending Review',
      })),
    [visible, effLevelByRef],
  );

  const filtered = useMemo(() => {
    const matched = rows.filter((r) => matchesFacets(r, facets)).map((r) => r.tx);
    const refs = new Set(matched.map((t) => t.referenceNo));
    // The transactions the reviewer self-declared — every finding on one of these is
    // cleared by that declaration (Lindiwe, 16 Aug 2026). Read from the stored level /
    // gap status, so it is a durable signal and no findings→tx→findings cycle.
    const sdRefs = buildSelfDeclaredRefs(matched);
    // The findings we IDENTIFIED: genuine, distinct non-compliant criteria (the one
    // definition shared with the report + register) — additional-comments notes and
    // duplicate (transaction × criterion) rows excluded — scoped to the in-view refs.
    const identified = genuineFindings(findings).filter((f) => f.referenceNo && refs.has(f.referenceNo));
    // Still OUTSTANDING: not on a self-declared transaction, and not self-declared or
    // resolved on its own. The scorecard reads this; the addressed routes to closure
    // are reconciled beside it.
    const outstanding = identified.filter((f) => findingOpen(f, sdRefs.has(f.referenceNo ?? '')));
    return { rows: matched, identified, outstanding, sdRefs };
  }, [rows, findings, facets]);

  const view = useMemo(() => {
    const kpis = computeDashboard(filtered.rows, effLevelByRef);
    const summary = summariseFindings(filtered.identified, filtered.sdRefs);
    return {
      kpis,
      identified: summary.identified,
      outstanding: summary.outstanding,
      selfDeclared: summary.selfDeclared,
      resolved: summary.resolved,
      // Breaches carry the addressed/outstanding split for the stacked bar; the
      // headline count is `outstanding`. Themes are current-risk analytics → the
      // outstanding set. Division/coverage analytics stay on the full population.
      breaches: breachBreakdown(filtered.identified, filtered.sdRefs),
      recurringThemes: recurringFindingsByTheme(filtered.outstanding),
      divisions: divisionStats(filtered.rows),
    };
  }, [filtered, effLevelByRef]);

  const donutData = useMemo(
    () =>
      DISTRIBUTION_LEVELS.filter((level) => (view.kpis.byLevel[level]?.count ?? 0) > 0).map(
        (level) => ({
          name: level,
          value: view.kpis.byLevel[level]?.count ?? 0,
          colour: levelColour(level),
        }),
      ),
    [view.kpis],
  );

  const breachData = useMemo(
    () =>
      view.breaches.map((b) => ({
        label: b.label,
        // Outstanding is highlighted (the breach count); addressed is muted context.
        open: b.outstanding,
        closed: b.addressed,
        colour: BREACH_COLOUR[b.key](colors),
      })),
    [view.breaches, colors],
  );

  const divisionData = useMemo(
    () =>
      view.divisions.slice(0, 6).map((d) => ({
        label: d.division,
        value: Math.round(d.riskIndex * 100) / 100,
        colour: d.riskIndex >= 2 ? colors.bad : d.riskIndex >= 1 ? colors.warn : colors.accent,
      })),
    [view.divisions, colors],
  );

  // Recurring Findings (1b): each curated theme as a bar, coloured from a stable
  // rotating palette so the chart reads cleanly in light + dark.
  const recurringData = useMemo(() => {
    const palette = [colors.bad, colors.warn, colors.accent, colors.ok, colors.house, colors.na];
    return view.recurringThemes.map((t, i) => ({
      label: t.theme,
      value: t.count,
      colour: palette[i % palette.length],
    }));
  }, [view.recurringThemes, colors]);
  const topTheme = view.recurringThemes[0];

  // The panel behind whichever figure was clicked, from the SAME filtered rows
  // and findings the cards are drawn from — so a drill can never disagree with
  // the number it was opened from.
  const drill = useMemo(
    () =>
      drillKey
        ? buildDrill(drillKey, {
            rows: rows.filter((r) => matchesFacets(r, facets)),
            // Drilling a scorecard figure lands on its OUTSTANDING findings, so a
            // drill can never disagree with the number it was opened from.
            findings: filtered.outstanding,
            facets,
          })
        : null,
    [drillKey, rows, facets, filtered.outstanding],
  );

  const isLoading = transactionsQuery.isLoading || findingsQuery.isLoading;
  const error = transactionsQuery.error ?? findingsQuery.error;

  if (isLoading) return <LoadingState message="Loading dashboard…" />;
  if (error) {
    return (
      <ErrorState
        message="Could not load the dashboard."
        detail={error instanceof Error ? error.message : String(error)}
        onRetry={() => {
          void transactionsQuery.refetch();
          void findingsQuery.refetch();
        }}
      />
    );
  }
  if (transactions.length === 0) {
    return <EmptyState message="No transactions have been captured yet." />;
  }

  const k = view.kpis;
  const inView = k.totalTransactions;
  const reviewedPct = inView > 0 ? Math.round((k.reviewed / inView) * 100) : 0;
  const recurring = [...view.breaches].sort((a, b) => b.outstanding - a.outstanding)[0];

  // The scope note that stamps every exported dashboard, so a division-scoped file
  // is self-describing. Cross-division roles see everything; the others are scoped.
  const scopeNote = (() => {
    const role = grant?.role;
    if (role === 'admin' || role === 'manager' || role === 'dataCoordinator')
      return 'All divisions (cross-division view)';
    const div = (grant?.division ?? '').trim();
    return div
      ? `Division: ${div} (${role === 'teamLead' ? 'team-lead' : 'reviewer'} view)`
      : 'Allocated transactions only';
  })();

  async function exportDashboard(kind: 'pptx' | 'pdf' | 'xlsx'): Promise<void> {
    if (!canExport) {
      toast.show(
        'Dashboard downloads are restricted to managers, team leads and administrators.',
        'error',
      );
      return;
    }
    setBusy(kind);
    try {
      const model = buildDashboardExportModel({
        kpis: view.kpis,
        findings: {
          identified: view.identified,
          outstanding: view.outstanding,
          selfDeclared: view.selfDeclared,
          resolved: view.resolved,
        },
        breaches: view.breaches,
        themes: view.recurringThemes,
        divisions: view.divisions,
        facets,
        meta: {
          dateLong: new Date().toLocaleDateString('en-ZA', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }),
          generatedAtIso: new Date().toISOString(),
          generatedBy: grant?.email ?? 'unknown',
          scopeNote,
        },
      });
      const stem = `Eskom PARCC Dashboard - ${model.meta.dateLong}`
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .trim();
      if (kind === 'xlsx') {
        const { buildDashboardWorkbookSheets } = await import('./export/buildDashboardWorkbook');
        downloadWorkbook(`${stem}.xlsx`, workbookFromSheets(buildDashboardWorkbookSheets(model)));
      } else {
        const { downloadDashboardPptx, downloadDashboardPdf } = await import('./export/download');
        await (kind === 'pptx'
          ? downloadDashboardPptx(model, `${stem}.pptx`)
          : downloadDashboardPdf(model, `${stem}.pdf`));
      }
      toast.show('Dashboard exported.', 'success');
    } catch (err) {
      toast.show(
        `Could not export the dashboard: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="dashboard-filter-bar">
        <FacetFilterBar
          rows={rows}
          state={facets}
          onChange={setFacets}
          onReset={() => setFacets(EMPTY_FACETS)}
        />
      </div>

      <section className="dz-hero">
        <div className="dz-hero-main">
          <div className="dz-eyebrow">Eskom · Procurement Audit Recovery Programme</div>
          <h1>Compliance recovery cockpit</h1>
          <p>
            {inView > 0
              ? `${inView.toLocaleString('en-ZA')} records in view — review progress, irregular-expenditure exposure and regulatory breaches across Eskom procurement routes.`
              : 'No records match the current filters.'}
          </p>
        </div>
        {/* Right column: risk chip on top, export actions beneath — fills the
            hero's right side instead of leaving it empty. In Legacy the chip
            keeps its absolute top-right position (its rule is unchanged) and this
            column simply stacks below the text. */}
        <div className="dz-hero-side">
          <span className="dz-chip">
            <span className="dz-dot" /> Risk intensity · {riskBand(k.riskScore)}
          </span>
          {canExport && (
            <div
              className="actions"
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}
            >
              <Button size="sm" disabled={busy !== null} onClick={() => void exportDashboard('pptx')}>
                <Download size={15} aria-hidden /> {busy === 'pptx' ? 'Preparing…' : 'PowerPoint'}
              </Button>
              <Button size="sm" disabled={busy !== null} onClick={() => void exportDashboard('pdf')}>
                <Download size={15} aria-hidden /> {busy === 'pdf' ? 'Preparing…' : 'PDF'}
              </Button>
              <Button size="sm" disabled={busy !== null} onClick={() => void exportDashboard('xlsx')}>
                <Download size={15} aria-hidden /> {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
              </Button>
            </div>
          )}
        </div>
      </section>

      <div className="dz-statgrid">
        <button
          type="button"
          className="dz-stat"
          onClick={() => setDrillKey('kpi:population')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Population <Database size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{inView.toLocaleString('en-ZA')}</div>
          <div className="d">Transactions in scope</div>
        </button>
        <button
          type="button"
          className="dz-stat ok"
          onClick={() => setDrillKey('kpi:reviewed')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Reviewed <CheckCircle2 size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{k.reviewed.toLocaleString('en-ZA')}</div>
          <div className="d">{reviewedPct}% completion</div>
        </button>
        <button
          type="button"
          className="dz-stat warn"
          onClick={() => setDrillKey('kpi:findings')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Findings <ClipboardList size={16} className="ic" aria-hidden />
          </div>
          {/* The scorecard number is OUTSTANDING findings — declared and resolved
              findings do not register as outstanding (Lindiwe R9, 16 Aug 2026), so
              this reads as the work still to do. The caption reconciles the three-way
              split so nothing is hidden: identified = outstanding + self-declared +
              resolved (the closure model reworked from the 30 Jul hide). */}
          <div className="v">{view.outstanding.toLocaleString('en-ZA')}</div>
          <div className="d">
            outstanding · {view.selfDeclared.toLocaleString('en-ZA')} self-declared ·{' '}
            {view.resolved.toLocaleString('en-ZA')} resolved
          </div>
        </button>
        {/* Possible-IE exposure, split (Lindiwe, 27 Jul 2026). One combined figure hid the
            distinction her report turns on: IE still sitting in unresolved gaps is a live
            audit exposure, while IE carried by a self-declaration has been resolved through
            declaration but — in her words — "remains a risk to Eskom". These two tiles are
            the client slide's "Rand Value - Potential IE based on Unresolved Gaps" and
            "Rand value - Potential IE to be Self Declared". */}
        <button
          type="button"
          className="dz-stat bad"
          onClick={() => setDrillKey('kpi:ie')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Possible IE · unresolved <Banknote size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{formatRandCompact(k.possibleIeValueUnresolved) || 'R 0'}</div>
          <div className="d">
            {k.possibleIeCountUnresolved.toLocaleString('en-ZA')} transactions · gaps still open
          </div>
        </button>
        <button
          type="button"
          className="dz-stat warn"
          onClick={() => setDrillKey('kpi:ie-self-declared')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Possible IE · self-declared <Banknote size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{formatRandCompact(k.possibleIeValueSelfDeclared) || 'R 0'}</div>
          <div className="d">
            {k.possibleIeCountSelfDeclared.toLocaleString('en-ZA')} transactions · declared, not
            recovered
          </div>
        </button>
        {/* Outcome tiles (Lindiwe feedback Jun 23 2026, 5a; reconciled Jul 8 2026; split
            27 Jul 2026). An outcome only counts once a transaction is REVIEWED — an
            unreviewed row is Pending even if it already carries provisional findings — so
            these four are the reviewed subset and reconcile exactly: Reviewed = Compliant +
            Non-Compliant + Self-Declared + Anomalies (N/A or to-be-deleted). Compliance itself is the
            EFFECTIVE level (findings + strict-rule override), the same the Transactions
            register shows. */}
        <button
          type="button"
          className="dz-stat ok"
          onClick={() => setDrillKey('kpi:compliant')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Compliant <CheckCircle2 size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{k.compliant.toLocaleString('en-ZA')}</div>
          <div className="d">reviewed · assessed Compliant</div>
        </button>
        <button
          type="button"
          className="dz-stat bad"
          onClick={() => setDrillKey('kpi:non-compliant')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Non-Compliant <XCircle size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{k.nonCompliant.toLocaleString('en-ZA')}</div>
          <div className="d">reviewed · not yet resolved</div>
        </button>
        <button
          type="button"
          className="dz-stat warn"
          onClick={() => setDrillKey('kpi:self-declared')}
          title="Open the transactions behind this figure"
        >
          <div className="lab">
            Self-Declared <FileCheck size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{k.selfDeclared.toLocaleString('en-ZA')}</div>
          <div className="d">reviewed · resolved by declaration</div>
        </button>
        <button
          type="button"
          className="dz-stat"
          onClick={() => setDrillKey('kpi:anomalies')}
          title="Open the transactions behind this figure"
        >

          <div className="lab">
            Anomalies <MinusCircle size={16} className="ic" aria-hidden />
          </div>
          <div className="v">{k.anomalies.toLocaleString('en-ZA')}</div>
          <div className="d">reviewed · not applicable or to be deleted</div>
        </button>
      </div>

      {/* Reconciliation strip — Reviewed = Compliant + Non-Compliant + Self-Declared + Anomalies, and the
          population splits cleanly into reviewed + pending. The identity Lindiwe asked to
          see hold (8 Jul 2026). */}
      <div
        className="dz-cap"
        style={{
          margin: '2px 0 18px',
          padding: '10px 14px',
          border: '1px solid var(--line)',
          borderRadius: 10,
        }}
      >
        <b style={{ color: 'var(--text)' }}>{inView.toLocaleString('en-ZA')}</b> in view ={' '}
        {k.reviewed.toLocaleString('en-ZA')} reviewed + {k.pending.toLocaleString('en-ZA')} pending
        review
        {'  ·  '}
        reviewed = {k.compliant.toLocaleString('en-ZA')} Compliant +{' '}
        {k.nonCompliant.toLocaleString('en-ZA')} Non-Compliant +{' '}
        {k.selfDeclared.toLocaleString('en-ZA')} Self-Declared +{' '}
        {k.anomalies.toLocaleString('en-ZA')} Anomalies
        {'  ·  '}
        gaps resolved = {k.compliant.toLocaleString('en-ZA')} Compliant +{' '}
        {k.selfDeclared.toLocaleString('en-ZA')} Self-Declared
      </div>

      <div className="dz-grid">
        <div className="dz-card">
          <div className="dz-card-head">
            <div>
              <h3>Compliance distribution</h3>
              <div className="dz-cap">Share of the population by compliance level</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <DistributionDonut
                data={donutData.map((d) => ({ name: d.name, value: d.value, colour: d.colour }))}
                centerValue={k.reviewed.toLocaleString('en-ZA')}
                centerLabel="Reviewed"
              />
            </div>
            <div className="dz-legend" style={{ flex: '1 1 200px' }}>
              {donutData.map((d) => (
                <button
                  type="button"
                  className="dz-lg"
                  key={d.name}
                  onClick={() => setDrillKey(`level:${d.name}`)}
                  title={`Open the ${d.name} transactions`}
                >
                  <span className="sw" style={{ background: d.colour }} />
                  {d.name}
                  <span className="n">{inView ? Math.round((d.value / inView) * 100) : 0}%</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="dz-card">
          <div className="dz-card-head">
            <div>
              <h3>Risk index</h3>
              <div className="dz-cap">Weighted exposure score (0–100)</div>
            </div>
          </div>
          <RiskGauge
            value={k.riskScore}
            max={100}
            ok={colors.ok}
            warn={colors.warn}
            bad={colors.bad}
            track={colors.surfaceRaised}
            bandLabel={riskBand(k.riskScore)}
          />
        </div>

        <div className="dz-card">
          <div className="dz-card-head">
            <div>
              <h3>Regulatory breaches</h3>
              <div className="dz-cap">
                Breach lines anchored to each framework · outstanding vs addressed
              </div>
            </div>
          </div>
          <StackedCategoryBar
            data={breachData}
            axisColour={colors.muted}
            gridColour={colors.surfaceRaised}
            closedColour={colors.na}
            height={170}
          />
          <div className="dz-drillrow">
            {view.breaches.map((b) => (
              <button
                key={b.key}
                type="button"
                className="btn sm ghost"
                onClick={() => setDrillKey(`breach:${b.key}`)}
                title={`Open the transactions behind ${b.label}`}
              >
                {b.label} · {b.outstanding.toLocaleString('en-ZA')}
              </button>
            ))}
          </div>
          <div
            className="dz-cap"
            style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}
          >
            {recurring && recurring.outstanding > 0 && (
              <div style={{ marginBottom: 6 }}>
                Leading category · <b style={{ color: 'var(--text)' }}>{recurring.label}</b> —{' '}
                {recurring.transactions.toLocaleString('en-ZA')} transactions
              </div>
            )}
            <div style={{ marginBottom: 6 }}>
              Addressed (resolved or self-declared) ·{' '}
              {view.breaches
                .map((b) => `${b.label} ${b.addressed.toLocaleString('en-ZA')}`)
                .join(' · ')}
            </div>
            {BREACH_SPLIT_FOOTNOTE}
          </div>
        </div>

        <div className="dz-card">
          <div className="dz-card-head">
            <div>
              <h3>Division risk profile</h3>
              <div className="dz-cap">Mean risk index by operating unit</div>
            </div>
          </div>
          <CategoryBar
            data={divisionData}
            axisColour={colors.muted}
            gridColour={colors.surfaceRaised}
            height={170}
          />
          <div className="dz-drillrow">
            {divisionData.map((d) => (
              <button
                key={d.label}
                type="button"
                className="btn sm ghost"
                onClick={() => setDrillKey(`division:${d.label}`)}
                title={`Open ${d.label}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="dz-card span2">
          <div className="dz-card-head">
            <div>
              <h3>Recurring findings</h3>
              <div className="dz-cap">
                Leading themes across the population, by transactions affected
                {view.recurringThemes.length >= 8 ? ' · top 8' : ''}
              </div>
            </div>
          </div>
          {recurringData.length > 0 ? (
            <>
              <CategoryBar
                data={recurringData}
                axisColour={colors.muted}
                gridColour={colors.surfaceRaised}
                height={Math.max(180, recurringData.length * 34)}
              />
              <div className="dz-drillrow">
                {recurringData.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    className="btn sm ghost"
                    onClick={() => setDrillKey(`theme:${t.label}`)}
                    title={`Open the transactions exhibiting ${t.label}`}
                  >
                    {t.label} · {t.value.toLocaleString('en-ZA')}
                  </button>
                ))}
              </div>
              {topTheme && (
                <div
                  className="dz-cap"
                  style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}
                >
                  Most recurrent · <b style={{ color: 'var(--text)' }}>{topTheme.theme}</b> —{' '}
                  {topTheme.count.toLocaleString('en-ZA')} transactions
                </div>
              )}
            </>
          ) : (
            <div className="dz-cap">No findings in view to theme.</div>
          )}
        </div>
      </div>

      {/* Mounted only while a drill is open — it owns router + overlay machinery
          that has no business running when nothing is being drilled into. */}
      {drill && <DrillPanel model={drill} facets={facets} onClose={() => setDrillKey(null)} />}
    </>
  );
}
